import { useState, useCallback, useEffect, useContext } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  format,
  parseISO,
  formatISO,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { useHistory, useLocation } from 'react-router-dom';

import { getAllTeleconsultations } from 'api/teleconsultations';
import { filtersToQueryString } from 'helpers/filtering';
import EventCalendar from 'components/calendar/calendar';
import Modal from 'components/calendar/modals/teleconsultation';

import { parseDatetime } from 'helpers/timezone';
import { UserContext } from 'contexts/user';
import { nameDecorator } from 'helpers/name';

const TeleconsultationCalendar = () => {
  const history = useHistory();
  const location = useLocation();
  const [fetching, setFetching] = useState(true);
  const [view, setView] = useState('day');
  const [date, setDate] = useState(
    formatISO(new Date(), { representation: 'date' }),
  );
  const [allTeles, setAllTeles] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState();
  const { user } = useContext(UserContext);

  const fetchData = useCallback(async (date, view) => {
    let filterString;

    switch (view) {
      case 'day':
        filterString = filtersToQueryString({
          from: [format(startOfDay(parseISO(date)), 'yyyy-MM-dd')],
          to: [format(endOfDay(parseISO(date)), 'yyyy-MM-dd')],
        });
        break;
      case 'week':
        filterString = filtersToQueryString({
          from: [format(startOfWeek(parseISO(date)), 'yyyy-MM-dd')],
          to: [format(endOfWeek(parseISO(date)), 'yyyy-MM-dd')],
        });
        break;
      case 'month':
        filterString = filtersToQueryString({
          from: [format(startOfMonth(parseISO(date)), 'yyyy-MM-dd')],
          to: [format(endOfMonth(parseISO(date)), 'yyyy-MM-dd')],
        });
        break;
      default:
        filterString = filtersToQueryString({
          from: [format(startOfDay(parseISO(date)), 'yyyy-MM-dd')],
          to: [format(endOfDay(parseISO(date)), 'yyyy-MM-dd')],
        });
        break;
    }

    const data = await getAllTeleconsultations(0, filterString, 0);

    setAllTeles(data.items);
    setFetching(false);
  }, []);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const view = search.get('view') || 'day';
    const date =
      search.get('date') || formatISO(new Date(), { representation: 'date' });
    setView(view);
    setDate(date);

    fetchData(date, view);
  }, [location, fetchData]);

  const handleNavigate = (date, view) => {
    const formattedDate = formatISO(date, {
      representation: 'date',
    });

    history.push({
      pathname: location.pathname,
      search: `?view=${view}&date=${formattedDate}`,
    });
  };

  const handleSelectEvent = (event) => {
    setShowEventModal(true);
    setCurrentEvent(event);
  };

  const handleViewChange = (view) => {
    const formattedDate = formatISO(new Date(), {
      representation: 'date',
    });

    history.push({
      pathname: location.pathname,
      search: `?view=${view}&date=${formattedDate}`,
    });
  };

  const handleShowMore = (_, date) => {
    const formattedDate = formatISO(date, {
      representation: 'date',
    });

    history.push({
      pathname: location.pathname,
      search: `?view=day&date=${formattedDate}`,
    });
  };

  let events;
  if (user.role === 'nurse') {
    events = allTeles
      .filter((t) => t.nurse && t.nurse.id === user.id && t.stage === 'nurse')
      .map((t) => ({
        id: t.id,
        title: `${nameDecorator(
          t.customer?.firstName,
          t.customer?.lastName,
        )}: ${t.name}`,
        start: parseDatetime(t.startTime),
        end: parseDatetime(t.endTime),
        status: t.status,
        cancelled: t.cancelled,
        cancellationReason: t.cancellationReason,
        nextAction: t.nextAction,
        customer: t.customer,
        doctor: t.doctor,
        stage: t.stage,
        backgroundColor: 'rgb(221, 214, 254)',
      }));
  } else {
    events = allTeles.map((t) => ({
      id: t.id,
      title: `${nameDecorator(t.customer?.firstName, t.customer?.lastName)}: ${
        t.name
      }`,
      start: parseDatetime(t.startTime),
      end: parseDatetime(t.endTime),
      status: t.status,
      cancelled: t.cancelled,
      cancellationReason: t.cancellationReason,
      nextAction: t.nextAction,
      customer: t.customer,
      doctor: t.doctor,
      stage: t.stage,
      backgroundColor:
        t.stage === 'first'
          ? '#DBEAFE'
          : t.stage === 'second'
          ? '#FEF3C7'
          : t.stage === 'nurse'
          ? 'rgb(221, 214, 254)'
          : '#DCFCE7', //renewals
    }));
  }

  return (
    <>
      <main className="th-main">
        <section className="th-canvas">
          <header className="th-canvas-header post-header members-header">
            <h2 className="th-canvas-title" data-test-screen-title>
              <span className="active">Agenda</span>
            </h2>
          </header>
          <EventCalendar
            date={date}
            events={events}
            view={view}
            onSelectEvent={handleSelectEvent}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            onShowMore={handleShowMore}
            loading={fetching}
          />
        </section>
      </main>
      {showEventModal && (
        <Modal
          event={currentEvent}
          onClose={() => setShowEventModal(false)}
          onClickOutside={() => setShowEventModal(false)}
        />
      )}
    </>
  );
};

export default TeleconsultationCalendar;
